import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { Button, Text, Label, Input, Select, Option, FlexBox, Title, AnalyticalTable, Dialog, Wizard, WizardStep, Icon } from '@ui5/webcomponents-react';
import Navbar from './Navbar';
import SideMenu from './SideMenu';
import { rightAlignedNumberStyle, formattedNumberCell, formatNumberWithComma, parseToNumber } from '../utils/validator';
import useAutoTranslate from '../hooks/useAutoTranslate';

const EditASN = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [asnDetail, setAsnDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [newShipmentInstructions, setNewShipmentInstructions] = useState('');
    const [newDeliveryDate, setNewDeliveryDate] = useState('');
    const [newPackageQty, setNewPackageQty] = useState('');
    const [newTotalVolume, setNewTotalVolume] = useState(0);
    const [newTotalWeight, setNewTotalWeight] = useState(0);
    const [newChargeType, setNewChargeType] = useState('');
    const [newTransportMedium, setnewTransportMedium] = useState('');
    const [purchaseOrder, setPurchaseOrder] = useState(null);
    const [bultos, setBultos] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedBulto, setSelectedBulto] = useState(null);
    const [combinedPackingLists, setCombinedPackingLists] = useState([]);
    const [newShipmentType, setNewShipmentType] = useState('');
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState('');
    const [disponibles, setDisponibles] = useState([]);
    const [deletedPackageIds, setDeletedPackageIds] = useState([]);
    const [selectedStep, setSelectedStep] = useState(0);
    const { autoT } = useAutoTranslate();

    // Opciones para Tipo de Carga
    const chargeTypeOptions = [
        { key: '', text: autoT('Seleccionar') },
        { key: 'A granel', text: autoT('Carga General') },
        { key: 'Caja Carton', text: autoT('Producto Quimico') },
        { key: 'con Antigolpes', text: autoT('Sustancias Peligrosas') },
    ];

    // Opciones para Tipo de Transporte
    const transportMediumOptions = [
        { key: '', text: autoT('Seleccionar') },
        { key: 'Camioneta', text: autoT('Camioneta') },
        { key: 'Furgon o Utilitario', text: autoT('Furgon o Utilitario') },
        { key: 'Camion menor a 1 Tonelada', text: autoT('Equipo 3/4') },
        { key: 'Camion 1,5 Toneladas', text: autoT('Equipo 1,5 Toneladas') },
        { key: 'Camion 3,5 Toneladas', text: autoT('Equipo 3,5 Toneladas') },
        { key: 'Camion 15 Toneladas', text: autoT('Equipo 15 Toneladas') },
        { key: 'Camion 17 Toneladas', text: autoT('Equipo 17 Toneladas') },
        { key: 'Camion 28 Toneladas', text: autoT('Equipo 28 Toneladas') },
    ];

    // Opciones para Tipo de Envío
    const shipmentTypeOptions = [
        { key: '', text: autoT('Seleccionar') },
        { key: 'A granel', text: autoT('A granel') },
        { key: 'Carton', text: autoT('Caja cartón') },
        { key: 'con Antigolpes', text: autoT('Con antigolpes') },
        { key: 'Caja de Madera con Antigolpes', text: autoT('Caja de madera con antigolpes') },
        { key: 'Embalaje con marca Fragil', text: autoT('Embalaje con marca "Frágil"') },
        { key: 'Embalaje con marca No Mojar', text: autoT('Embalaje con marca "No Mojar"') },
        { key: 'Embalaje con marca Peligroso', text: autoT('Embalaje con marca "Peligroso"') },
        { key: 'Embalaje estándar del Proveedor', text: autoT('Embalaje estándar del proveedor') },
        { key: 'Embalaje Impermeable', text: autoT('Embalaje impermeable') },
        { key: 'Embalaje de Exportación', text: autoT('Embalaje de exportación') },
        { key: 'Jaba de Madera', text: autoT('Jaba de madera') },
        { key: 'Embalaje ver Clausula', text: autoT('Embalaje ver cláusula') },
        { key: 'Caja de Madera', text: autoT('Caja de madera') },
        { key: 'Marcado Mantener Aislado', text: autoT('Marcado "Mantener Aislado"') },
        { key: 'Pallet', text: autoT('Pallet') },
        { key: 'Container', text: autoT('Container') },
    ];

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const fetchASNDetail = async () => {
        try {
            const response = await axiosInstance.get(`asn-documents/${id}`);
    
            if (!response || !response.data || !response.data.data) {
                throw new Error(autoT("Invalid response format"));
            }
    
            const asnData = response.data.data;
    
            setAsnDetail(asnData);
            setNewShipmentInstructions(asnData.shipmentInstructions);
            setNewDeliveryDate(asnData.deliveryDate);
            setNewPackageQty(asnData.packageQty);
            setNewTotalVolume(asnData.totalVolume);
            setNewTotalWeight(asnData.totalWeight);
            setNewChargeType(asnData.chargeType || '');
            setnewTransportMedium(asnData.transportMedium || '');
            setNewShipmentType(asnData.shipmentType || '')
    
            // Inicializar bultos con los datos de asnDetail.packages
            if (Array.isArray(asnData.packages)) {
                const initializedBultos = asnData.packages.map(pkg => {
                    const firstPackingList = pkg.packingLists[0] || {};
                    const altura = firstPackingList.height || 0;
                    const largo = firstPackingList.length || 0;
                    const ancho = firstPackingList.width || 0;

                    const bulto = {
                        id: pkg.id,
                        packageNumber: pkg.packageNumber,
                        weight: pkg.weight || 0,
                        altura: altura,
                        largo: largo,
                        ancho: ancho,
                        volumen: calcularVolumen(altura, largo, ancho)
                    };
                    return bulto;
                });
                setBultos(initializedBultos);
            } else {
                setBultos([]);
            }
    
            if (asnData.purchaseOrderId) {
                await fetchPurchaseOrder(asnData.purchaseOrderId);
            } else {
                console.warn(autoT("No purchaseOrderId found in ASN data"));
            }
    
        } catch (err) {
            console.error(autoT('Error fetching ASN details:'), err);
            setError(autoT('Error fetching ASN details'));
        } finally {
            setLoading(false);
        }
    };

    const handleStepChange = (event) => {
        const nextStep = event.detail.step - 1;
        setSelectedStep(nextStep);
    };
    
    const fetchPurchaseOrder = async (purchaseOrderId) => {
        if (!purchaseOrderId) return;
        try {
            const response = await axiosInstance.get(`purchase-orders/${purchaseOrderId}`);
            const orderNumberResponse = await axiosInstance.get(`purchase-orders/order-number/${response.data.data.orderNumber}`);
            setPurchaseOrder(orderNumberResponse.data.data);
        } catch (err) {
            setError(autoT('Error fetching PurchaseOrder'));
        }
    };

    const itemColumns = [
        { Header: autoT('Item PO'), accessor: 'lineId' },
        { Header: autoT('Código de material'), accessor: 'materialCode' },
        { Header: autoT('Nombre'), accessor: 'description' },
        { Header: autoT('Unidad'), accessor: 'unit' },
        {
          Header: autoT('Precio Unitario'),
          accessor: 'pricePerUnit',
          Cell: ({ value }) => (
            <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
          ),
          hAlign: "End"
        },
        {
          Header: autoT('Cantidad Pendiente'),
          accessor: 'quantityPending',
          Cell: ({ value }) => (
            <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
          ),
          hAlign: "End"
        },
        {
          Header: autoT('Cantidad'),
          accessor: 'quantity',
          Cell: ({ row }) => {
            const lineId = row.original.lineId;
            const existingItem = asnDetail.items.find(item => item.lineId === lineId);
            const initialQuantity = existingItem?.quantity || 0;
            const [tempQuantity, setTempQuantity] = useState(formatNumberWithComma(initialQuantity));
            const [originalQuantity, setOriginalQuantity] = useState(initialQuantity);
            
            useEffect(() => {
                setTempQuantity(formatNumberWithComma(initialQuantity));
                setOriginalQuantity(initialQuantity);
              }, [existingItem]);

            return (
                <Input
                type="text"
                    value={tempQuantity}
                onInput={(e) => {
                    const raw = e.target.value;
                    const cleaned = raw.replace(/[^0-9,]/g, '');
                    setTempQuantity(cleaned);
                }}
                onBlur={() => {
                    const parsedValue = parseToNumber(tempQuantity);
                    const formatted = formatNumberWithComma(parsedValue);
                    const previousValue = parseToNumber(originalQuantity);
                    const difference = parsedValue - previousValue;

                    const quantityPending = purchaseOrder.items.find(item => item.lineId === row.original.lineId)?.quantityPending || 0;
                    const updatedPendingQuantity = quantityPending - difference;

                    const updatedItems = asnDetail.items.map(item => {
                    if (item.lineId === lineId) {
                        return {
                        ...item,
                        quantity: parsedValue
                        };
                                }
                                return item;
                            });

                    const updatedItems2 = purchaseOrder.items.map(item => {
                    if (item.lineId === lineId) {
                        return {
                        ...item,
                        quantityPending: updatedPendingQuantity
                        };
                    }
                    return item;
                    });

                            setAsnDetail({ ...asnDetail, items: updatedItems });
                    setPurchaseOrder({ ...purchaseOrder, items: updatedItems2 });

                    setDisponibles(prev =>
                    prev.map(item => {
                        if (item.lineId === row.original.lineId) {
                        return {
                            ...item,
                            quantityLeft: item.quantityLeft + difference
                        };
                        }
                        return item;
                    }).filter(item => item.quantityLeft > 0)
                    );

                    if (!existingItem && parsedValue > 0) {
                    const orderItem = purchaseOrder.items.find(item => item.lineId === lineId);
                            const newItem = {
                                lineId: lineId,
                        materialCode: orderItem?.materialCode || '',
                                description: orderItem?.description || '',
                                unit: orderItem?.unit || null,
                        pricePerUnit: orderItem?.pricePerUnit || 0,
                        quantityPending: parsedValue,
                        asnSentQuantity: parsedValue,
                        quantity: parsedValue
                    };
                     setAsnDetail(prev => ({
                         ...prev,
                        items: [...prev.items, newItem]
                     }));
                    }
                     
                    setOriginalQuantity(parsedValue);
                    setTempQuantity(formatted);
                }}
                style={{ width: '100px', textAlign: 'right' }}
                />

            );
          },
          hAlign: "End"
        },
        {
            Header: autoT('Precio Total'),
            accessor: 'totalPrice',
            Cell: ({ row }) => {
                const pricePerUnit = row.original.pricePerUnit || 0;
                const quantity = asnDetail.items.find(item => item.lineId === row.original.lineId)?.quantity || 0;
                const totalPrice = (pricePerUnit * quantity);
                return (
                  <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(totalPrice)}</Text>
                );
            }
        },
    ];

    const calcularVolumen = (altura, largo, ancho) => {
        const alturaNum = parseFloat(altura) || 0;
        const largoNum = parseFloat(largo) || 0;
        const anchoNum = parseFloat(ancho) || 0;
        
        const volumen = alturaNum * largoNum * anchoNum;
        
        return volumen;
    };

    const handleBultoUpdate = (bultoNumber, field, value) => {
        setBultos(prev => {
            const updatedBultos = prev.map(bulto => {
                if (bulto.packageNumber === bultoNumber) {
                    const parsedValue = parseToNumber(value);
                    const newBulto = { ...bulto, [field]: parsedValue };
                    
                    if (['altura', 'largo', 'ancho'].includes(field)) {
                        const altura = field === 'altura' ? parsedValue : bulto.altura;
                        const largo = field === 'largo' ? parsedValue : bulto.largo;
                        const ancho = field === 'ancho' ? parsedValue : bulto.ancho;
                        
                        newBulto.volumen = calcularVolumen(altura, largo, ancho);
                    }
                    
                    return newBulto;
                }
                return bulto;
            });
            
            return updatedBultos;
        });
    };

    const buildRequestBody = () => {
        return {
            asnDate: asnDetail?.asnDate,
            asnStatus: "Modified",
            deliveryDate: newDeliveryDate || asnDetail?.deliveryDate,
            deliveryTime: newDeliveryDate ? newDeliveryDate + "T00:00:00" : asnDetail?.deliveryTime,
            totalWeight: bultos.reduce((total, bulto) => total + (bulto.weight || 0), 0),
            totalVolume: bultos.reduce((total, bulto) => {
                const volumen = parseToNumber(bulto.volumen || 0);
                return total + volumen;
            }, 0),
            transportMedium: newTransportMedium || asnDetail?.transportMedium,
            shipmentType: newShipmentType || asnDetail?.shipmentType,
            chargeType: newChargeType || asnDetail?.chargeType,
            packageQty: bultos.length,
            vehiclePlate: asnDetail?.vehiclePlate,
            shipmentInstructions: newShipmentInstructions || asnDetail?.shipmentInstructions,
            eeNumber: asnDetail?.eeNumber,
            createdBy: asnDetail?.createdBy ||"315f91a9-b4ea-467e-929d-feb6bf447fb2",
            modifiedBy: asnDetail?.modifiedBy ||"315f91a9-b4ea-467e-929d-feb6bf447fb2",
            createdDate: asnDetail?.createdDate,
            modifiedDate: new Date().toISOString(),
            items: asnDetail?.items.map(item => ({
                id: item.id,
                asnDocumentId: asnDetail?.id,
                lineId: item.lineId,
                materialCode: item.materialCode,
                description: item.description,
                quantity: item.quantity,
                unit: item.unit,
                pricePerUnit: item.pricePerUnit,
                totalPrice: (item.pricePerUnit * item.quantity),
                receivedQuantity: item.receivedQuantity || null,
                quantityReverse: item.quantityReverse || null,
                quantityPending: item.quantityPending || null,
                asnSentQuantity: item.asnSentQuantity || null,
                positionPo: item.positionPo || null
            })),
            packages: bultos.map(bulto => ({
                id:bulto.id  || null,
                asnDocumentId: asnDetail?.id,
                packageNumber: bulto.packageNumber || null,
                weight: bulto.weight,
                dimensions: `${bulto.altura}x${bulto.largo}x${bulto.ancho}`,
                volume: parseToNumber(bulto.volumen),
                length: bulto.largo,
                width: bulto.ancho,
                height: bulto.altura,
                packingLists: combinedPackingLists
                    .filter(item => item.packageNumber === bulto.packageNumber)
                    .map(packingList => ({
                        itemDescription: packingList.itemDescription,
                        asnDocumentId: asnDetail?.id,
                        asnPackageId: packingList.asnPackageId,
                        packageNumber: null,
                        id: packingList.id  || null,
                        packageCodebars: packingList.packageCodebars || "1",
                        weight: bulto.weight,
                        volume: parseToNumber(bulto.volumen),
                        length: bulto.largo,
                        width: bulto.ancho,
                        height: bulto.altura,
                        quantity: packingList.quantity
                    }))
            }))
        };
    };

    const handleDeleteBulto = (bulto) => {
        if (!bulto.packageNumber.includes("Bulto")) {
            setDeletedPackageIds(prev => [...prev, bulto.id]);
        }

        const packingListsToUpdate = combinedPackingLists.filter(item => item.packageNumber === bulto.packageNumber);
        
        setDisponibles(prev => {
            return prev.map(item => {
                const packingListItem = packingListsToUpdate.find(pl => 
                    pl.lineId === item.lineId
                );
                if (packingListItem) {
                    return { 
                        ...item, 
                        quantityLeft: item.quantityLeft + packingListItem.quantity
                    };
                }
                return item;
            }).concat(
                packingListsToUpdate
                    .filter(pl => !prev.some(item => item.lineId === pl.lineId))
                    .map(pl => ({
                        lineId: pl.lineId,
                        materialCode: pl.itemDescription,
                        quantityLeft: pl.quantity
                    }))
            );
        });

        setBultos(prev => prev.filter(item => item.packageNumber !== bulto.packageNumber));
    };

    const handleUpdateASN = async () => {
        const errorMessage = validateForm();
        if (errorMessage) {
            setErrorDialogMessage(errorMessage);
            setIsErrorDialogOpen(true);
            return;
        }

        try {
            for (const packageId of deletedPackageIds) {
                await axiosInstance.delete(`/packages/${packageId}`);
            }

            const requestBody = buildRequestBody();
            const jsonRequestBody = JSON.stringify(requestBody);

            await axiosInstance.put(`/asn-documents/${id}`, jsonRequestBody, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            navigate(`/asn/${id}`);
        } catch (error) {
            console.error(autoT('Error updating ASN:'), error);
        }
    };

    const handleOpenDialog = (bulto) => {
        setSelectedBulto(bulto);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedBulto(null);
    };

    const handleAddBulto = () => {
        const newBulto = {
            packageNumber: `${autoT('Bulto')} ${bultos.length + 1}`,
            weight: 0,
            altura: 0,
            largo: 0,
            ancho: 0,
            volumen: 0,
        };
        setBultos(prev => [...prev, newBulto]);
    };

    const handleAddMaterial = (material) => {
        if (!selectedBulto) {
            console.error(autoT("No hay bulto seleccionado."));
            return;
        }
    
        const materialsInSelectedBulto = combinedPackingLists.filter(
            item => item.packageNumber === selectedBulto.packageNumber
        );
    
        const exists = materialsInSelectedBulto.some(
            item => item.lineId === material.lineId
        );
        if (exists) {
            console.error(autoT('El material {{materialCode}} ya existe en el bulto {{packageNumber}}.', { 
                materialCode: material.materialCode, 
                packageNumber: selectedBulto.packageNumber 
            }));
            return;
        }
    
        const quantityToAdd = material.quantityLeft > 0 ? material.quantityLeft : 1;
    
        const newMaterial = {
            lineId: material.lineId,
            itemDescription: material.materialCode,
            quantity: quantityToAdd,
            packageNumber: selectedBulto.packageNumber,
        };
    
        setCombinedPackingLists(prev => [...prev, newMaterial]);
    
        setDisponibles(prev => {
            return prev.map(item => {
                if (item.lineId === material.lineId) {
                    return {
                        ...item,
                        quantityLeft: item.quantityLeft - quantityToAdd
                    };
                }
                return item;
            })
        });
    };

    useEffect(() => {
        fetchASNDetail();
    }, [id]);

    useEffect(() => {
        if (asnDetail && purchaseOrder) {
            const packingListMap = {};
            asnDetail.packages.forEach(pkg => {
                pkg.packingLists.forEach(item => {
                    // Usar lineId si está disponible, sino materialCode (itemDescription)
                    const key = item.lineId
                    if (!packingListMap[key]) {
                        packingListMap[key] = 0;
                    }
                    packingListMap[key] += item.quantity;
                });
            });

            const aggregatedPackingLists = Object.entries(packingListMap).map(([key, quantity]) => ({
                lineId: key,
                itemDescription: key,
                quantity
            }));

            const materialsToAdd = asnDetail.items.map(item => {
                const packingItem = aggregatedPackingLists.find(packingItem => 
                    packingItem.lineId === item.lineId
                );
                
                const totalPackingQuantity = packingItem ? packingItem.quantity : 0;
                const totalAsnQuantity = item.quantity;

                const quantityLeft = totalPackingQuantity - totalAsnQuantity;

                return {
                    lineId: item.lineId,
                    materialCode: item.materialCode,
                    quantityLeft: quantityLeft
                };
            });

            const invertedMaterials = materialsToAdd
                .map(item => ({
                    lineId: item.lineId,
                    materialCode: item.materialCode,
                    quantityLeft: -item.quantityLeft
                }));

            setDisponibles(invertedMaterials);

            const newCombinedPackingLists = asnDetail.packages?.flatMap(pkg => pkg.packingLists) || [];
            setCombinedPackingLists(newCombinedPackingLists);
        }
    }, [asnDetail, purchaseOrder]);

    const validateForm = () => {
        if (!newShipmentInstructions) {
            return autoT("Las instrucciones de despacho es obligatoria.");
        }
        if (!newDeliveryDate) {
            return autoT("La fecha de entrega es obligatoria.");
        }
        if (bultos.length === 0) {
            return autoT("Debes agregar al menos un bulto.");
        }
        if (!newTotalVolume || newTotalVolume <= 0) {
            return autoT("El volumen debe ser un número positivo.");
        }
        if (!newTotalWeight || newTotalWeight <= 0) {
            return autoT("El peso debe ser un número positivo.");
        }
        if (!newChargeType) {
            return autoT("Debes seleccionar un tipo de carga.");
        }
        if (!newTransportMedium) {
            return autoT("Debes seleccionar un medio de transporte.");
        }
        if (!newShipmentType) {
            return autoT("Debes seleccionar un tipo de envío.");
        }

        for (const item of purchaseOrder.items) {
            if (item.quantityPending < 0) {
                return autoT("Ninguna cantidad pendiente puede ser negativa.");
            }
        }

        const totalBultosVolume = bultos.reduce((total, bulto) => total + (parseToNumber(bulto.volumen) || 0), 0);
        if (totalBultosVolume !== parseToNumber(newTotalVolume)) {
            return autoT("El volumen total de los bultos debe ser igual al volumen del Notificación.");
        }

        const totalBultosWeight = bultos.reduce((total, bulto) => total + (bulto.weight || 0), 0);
        if (totalBultosWeight !== parseToNumber(newTotalWeight)) {
            return autoT("El peso total de los bultos debe ser igual al peso del Notificación.");
        }

        const hasNonZeroQuantityLeft = disponibles.some(item => item.quantityLeft !== 0);
        if (hasNonZeroQuantityLeft) {
            return autoT("Faltan o sobran materiales en los bultos");
        }

        return null;
    };

    const handleTotalVolumeChange = (event) => {
        const value = event.target.value.replace(/[^0-9,]/g, '');
        const parsedValue = parseToNumber(value);
        setNewTotalVolume(parsedValue);
    };

    const handleTotalWeightChange = (event) => {
        const value = event.target.value.replace(/[^0-9,]/g, '');
        const parsedValue = parseToNumber(value);
        setNewTotalWeight(parsedValue);
    };

    const totalBultosVolume = bultos.reduce((total, bulto) => total + (parseToNumber(bulto.volumen) || 0), 0);
    const totalBultosWeight = bultos.reduce((total, bulto) => total + (bulto.weight || 0), 0);

    if (loading) return <Text>{autoT('Cargando Notificacion...')}</Text>;
    if (error) return <Text>{autoT('Error:')} {error}</Text>;

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100vh' 
        }}>
            <Navbar toggleSidebar={toggleSidebar} />
            <div style={{ 
                display: 'flex',
                flex: 1,
                marginTop: '68px'
            }}>
                <SideMenu isOpen={sidebarOpen} />

                <div style={{ 
                    flex: 1,
                    marginLeft: sidebarOpen ? '250px' : '0',
                    transition: 'margin-left 0.3s',
                    padding: '20px'
                }}>
                 <h2>{autoT('Editar Notificación N°')} {asnDetail?.asnNumber}</h2>

                <Wizard 
              onStepChange={handleStepChange} 
              style={{ 
                height: 'calc(100vh - 88px)',
                width: '100%'
              }}
            > 
             <WizardStep
                icon="edit"
                titleText={autoT("Editar datos")}
                selected={selectedStep === 0}
              >

                    
                    <Title level="H5" style={{ marginBottom: '1rem' }}>{autoT('Información de envío')}</Title>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '1rem' 
                    }}>

                        
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Instrucciones de despacho:')}</Label>
                            <Input value={newShipmentInstructions} onInput={(e) => setNewShipmentInstructions(e.target.value)} />
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Fecha de entrega:')}</Label>
                            <Input type="date" value={newDeliveryDate} onInput={(e) => setNewDeliveryDate(e.target.value)} />
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Número de bultos:')}</Label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text style={{ margin: '0 10px' }}>{bultos.length}</Text>
                                <Button onClick={handleAddBulto}>+</Button>
                            </div>
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Placa vehiculo:')}</Label>
                            <Input value={asnDetail?.vehiclePlate} onInput={(e) => setAsnDetail({ ...asnDetail, vehiclePlate: e.target.value })} />
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Volumen (m³):')}</Label>
                            <Input 
                                type="text" 
                                value={formatNumberWithComma(newTotalVolume)} 
                                onChange={handleTotalVolumeChange}
                                placeholder={autoT("Ingrese volumen total")}
                                style={{ textAlign: 'right' }}
                            />
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Peso (kg):')}</Label>
                            <Input 
                                type="text" 
                                value={formatNumberWithComma(newTotalWeight)} 
                                onChange={handleTotalWeightChange}
                                placeholder={autoT("Ingrese peso total")} 
                                style={{ textAlign: 'right' }}
                            />
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Tipo de carga:')}</Label>
                            <Text>{newChargeType || autoT('No seleccionado')}</Text>
                        </div>
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Tipo de transporte:')}</Label>

                        <Text>{newTransportMedium || autoT('No seleccionado')}</Text>
                        </div>
                        <div>
                        <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT('Tipo de bulto:')}</Label>

                        <Text>{newShipmentType || autoT('No seleccionado')}</Text>
                        </div>
                    </div>
                <div style={{ padding: '1rem' }}>
                  <Title level="H2" style={{ marginBottom: '1rem' }}>{autoT('Notificaciones de la orden de compra')}</Title>
                  
                  <div style={{ marginBottom: '2rem' }}>
                    <AnalyticalTable
                      data={purchaseOrder.items || []}
                      columns={itemColumns}
                      selectionMode="None"
                      scaleWidthMode="Smart"
                    />
                  </div>
                </div>
                </WizardStep>

                <WizardStep
                icon="attachment"
                titleText={autoT("Editar Bultos")}
                selected={selectedStep === 1}
              >
                <div style={{ 
                      backgroundColor: 'white',
                      padding: '1rem',
                      borderRadius: '8px',
                      boxShadow: '0 0 4px rgba(0,0,0,0.1)'
                    }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '1rem' 
                    }}>
                        <Title level="H3">{autoT('Dimensiones de los bultos')}</Title>
                    </div> 

                    <div style={{ marginBottom: '1rem' }}>
                        <Text>
                            {autoT('Volumen Total:')} {formatNumberWithComma(totalBultosVolume)} m³ / {formatNumberWithComma(newTotalVolume)} m³
                        </Text>
                        <Text>
                            {autoT('Peso Total:')} {formatNumberWithComma(totalBultosWeight)} kg / {formatNumberWithComma(newTotalWeight)} kg
                        </Text>
                    </div> 
                    
                    <AnalyticalTable
                        data={bultos}
                        columns={[
                            { Header: autoT('Nro Bulto'), accessor: 'packageNumber' },
                            { 
                                Header: autoT('Peso - kg'),
                                accessor: 'weight',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.weight)}
                                        onChange={(e) => {
                                            const newValue = e.target.value.replace(/[^0-9,]/g, '');
                                            handleBultoUpdate(row.original.packageNumber, 'weight', newValue);
                                        }}
                                        onBlur={(e) => {
                                            const formattedValue = formatNumberWithComma(parseToNumber(e.target.value));
                                            handleBultoUpdate(row.original.packageNumber, 'weight', formattedValue);
                                        }}
                                        style={{ textAlign: 'right' }}
                                    />
                                )
                            },
                            { 
                                Header: autoT('Altura - metros'),
                                accessor: 'altura',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.altura)}
                                        onChange={(e) => {
                                            const newValue = e.target.value.replace(/[^\d,\.]/g, '').replace(/\./g, ',');
                                            const commaCount = (newValue.match(/,/g) || []).length;
                                            if (commaCount <= 1) {
                                                handleBultoUpdate(row.original.packageNumber, 'altura', newValue);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const parsedValue = parseToNumber(e.target.value);
                                            handleBultoUpdate(row.original.packageNumber, 'altura', parsedValue);
                                        }}
                                        style={{ width: '100%', textAlign: 'right' }}
                                    />
                                )
                            },
                            { 
                                Header: autoT('Largo - metros'),
                                accessor: 'largo',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.largo)}
                                        onChange={(e) => {
                                            const newValue = e.target.value.replace(/[^\d,\.]/g, '').replace(/\./g, ',');
                                            const commaCount = (newValue.match(/,/g) || []).length;
                                            if (commaCount <= 1) {
                                                handleBultoUpdate(row.original.packageNumber, 'largo', newValue);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const parsedValue = parseToNumber(e.target.value);
                                            handleBultoUpdate(row.original.packageNumber, 'largo', parsedValue);
                                        }}
                                        style={{ width: '100%', textAlign: 'right' }}
                                    />
                                )
                            },
                            { 
                                Header: autoT('Ancho - metros'),
                                accessor: 'ancho',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.ancho)}
                                        onChange={(e) => {
                                            const newValue = e.target.value.replace(/[^\d,\.]/g, '').replace(/\./g, ',');
                                            const commaCount = (newValue.match(/,/g) || []).length;
                                            if (commaCount <= 1) {
                                                handleBultoUpdate(row.original.packageNumber, 'ancho', newValue);
                                            }
                                        }}onBlur={(e) => {
                                            // Al perder el foco, formatear correctamente
                                            const parsedValue = parseToNumber(e.target.value);
                                            handleBultoUpdate(row.original.packageNumber, 'ancho', parsedValue);
                                        }}
            style={{ width: '100%', textAlign: 'right' }}
            />
            )
            },
            {
            Header: autoT('Volumen - m³'),
            accessor: 'volumen',
            Cell: ({ row }) => (
                <Text style={rightAlignedNumberStyle()}>
                    {formatNumberWithComma(row.original.volumen || 0)}
                </Text>
            ),
            hAlign: "End"
            },
            {
            Header: autoT('Acciones'),
            accessor: 'acciones',
            Cell: ({ row }) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button onClick={() => handleOpenDialog(row.original)}>{autoT('Ver materiales')}</Button>
                    <Button 
                        style={{ backgroundColor: 'red', color: 'white' }} 
                        onClick={() => handleDeleteBulto(row.original)}
                    >
                    <Icon name="delete" style={{ color: 'white' }} />
                    </Button>
                </div>
            )
            }
            ]}
            selectionMode="None"
            scaleWidthMode="Smart"
            minRows={Math.min(bultos.length, 1)}
            visibleRows={Math.min(bultos.length, 10)}
            style={{ 
                marginBottom: '1rem' 
            }}
            header
            alternateRowColor
            />
            </div>
            </WizardStep>
            </Wizard>
            <div style={{ 
                position: 'sticky',
                bottom: '20px',
                marginTop: '2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: 'white',
                boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                zIndex: 100
            }}>
                <div style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '10px'
                }}>
                    <Button 
                        onClick={() => setSelectedStep(0)}
                        design="Emphasized"
                        disabled={selectedStep === 0}
                    >
                        {autoT('Anterior')}
                    </Button>
                    
                    <Button 
                        onClick={() => setSelectedStep(1)}
                        design="Emphasized"
                        disabled={selectedStep === 1}
                    >
                        {autoT('Siguiente')}
                    </Button>
                </div>
                
                <Button 
                    onClick={handleUpdateASN}
                    design="Positive"
                    style={{ marginLeft: 'auto' }}
                    icon="save"
                >
                    {autoT('Actualizar notificación')}
                </Button>
            </div>
            
            {isDialogOpen && (
                <Dialog 
                    open={isDialogOpen} 
                    onClose={handleCloseDialog}
                    style={{ width: '80%', maxWidth: '800px' }}
                >
                    <Title level="H3">{autoT('Materiales para')} {selectedBulto?.packageNumber}</Title>
            
                    <AnalyticalTable
                data={combinedPackingLists.filter(
                    (item) => item.packageNumber === selectedBulto?.packageNumber
                )}
                    columns={[
                { Header: autoT('Posición'), accessor: 'lineId' },
                { Header: autoT('Código de material'), accessor: 'itemDescription' },
                {
                Header: autoT('Nombre'),
                accessor: 'materialCode',
                Cell: ({ row }) => {
                    const material = asnDetail.items.find(
                    (item) => item.lineId === row.original.lineId
                    );
                    return material ? material.description : autoT('No disponible');
                },
                },
                        {
                            Header: autoT('Cantidad'),
                            accessor: 'quantity',
                            Cell: ({ row }) => {
                const [tempQuantity, setTempQuantity] = useState(
                formatNumberWithComma(row.original.quantity)
                );
            
                                return (
                                    <Input
                        type="text"
                                    value={tempQuantity}
                                    onInput={(e) => {
                        const raw = e.target.value;
                        const cleaned = raw.replace(/[^0-9,]/g, '');
                        setTempQuantity(cleaned);
                                    }}
                                    onBlur={() => {
                        const newValue = parseToNumber(tempQuantity);
                        const previousValue = parseToNumber(row.original.quantity);
                        const difference = newValue - previousValue;
            
                        const updatedPackingLists = combinedPackingLists.map((item) => {
                            if (
                            (item.lineId === row.original.lineId) &&
                            item.packageNumber === row.original.packageNumber
                            ) {
                                                        return { ...item, quantity: newValue };
                                                    }
                                                    return item;
                                                });
                                                setCombinedPackingLists(updatedPackingLists);
            
                        setDisponibles((prev) => {
                            let updated = prev.map((item) => {
                            if (item.lineId === row.original.lineId ) {
                                return {
                                ...item,
                                quantityLeft: item.quantityLeft - difference,
                                };
                            }
                            return item;
                            });
            
                            // Agregar si no existe
                            if (
                            !updated.some(
                                (item) => item.lineId === row.original.lineId
                            )
                            ) {
                            updated.push({
                                lineId: row.original.lineId,
                                materialCode: row.original.itemDescription,
                                quantityLeft: -difference,
                            });
                            }
            
                            return updated;
                        });
            
                        setTempQuantity(formatNumberWithComma(newValue));
                                    }}
                                    style={{ width: '100%', textAlign: 'right' }}
                                />
                                );
                },
                        hAlign: "End"
                        },
                    ]}
                    selectionMode="None"
                    scaleWidthMode="Smart"
                    style={{ height: '200px' }}
                />
                
                <Title level="H4">{autoT('Agregar Materiales Disponibles')}</Title>
            
                <AnalyticalTable
                data={disponibles}
                    columns={[
                        { Header: autoT('Posición'), accessor: 'lineId' },
                        { Header: autoT('Código de material'), accessor: 'materialCode' },
                        {
                Header: autoT('Nombre'),
                accessor: 'description',
                Cell: ({ row }) => {
                    const material = asnDetail.items.find(
                    (item) => item.lineId === row.original.lineId
                    );
                    return material ? material.description : autoT('No disponible');
                },
                },
                {
                    Header: autoT('Cantidad faltante'),
                    accessor: 'quantityLeft',
                    Cell: ({ row }) => {
                        let number = row.original.quantityLeft ?? 0;
                    
                        // Evitar mostrar -0.000, usar 0.000 en su lugar si está muy cerca de cero
                        if (Math.abs(number) < 0.0005) {
                        number = 0;
                        }
                    
                        return (
                        <Text style={rightAlignedNumberStyle()}>
                            {number.toLocaleString('es-CL', {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3
                            })}
                        </Text>
                        );
                    },
                    hAlign: "End"
                    },
                                            {
                Header: autoT('Acciones'),
                accessor: 'acciones',
                Cell: ({ row }) => (
                    <Button onClick={() => handleAddMaterial(row.original)}>{autoT('Agregar')}</Button>
                ),
                },
                    ]}
                    selectionMode="None"
                    scaleWidthMode="Smart"
                    style={{ height: '200px' }}
                />
                
                <Button onClick={handleCloseDialog}>{autoT('Cerrar')}</Button>
                </Dialog>
            )}
            
            
            </div>
            </div>
            {isErrorDialogOpen && (
                <Dialog 
                    open={isErrorDialogOpen} 
                    onClose={() => setIsErrorDialogOpen(false)} 
                    style={{ width: '80%', maxWidth: '400px' }}
                >
                    <Title level="H3">{autoT('Error')}</Title>
                    <Text>{errorDialogMessage}</Text>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <Button onClick={() => setIsErrorDialogOpen(false)}>{autoT('Cerrar')}</Button>
                    </div>
                </Dialog>
            )}
            
        </div>
    );
};

export default EditASN; 